// Chat V2 helpers for the web chat — the same rules the mobile app uses (src/features/messages/utils/chatMessages.ts
// in cvanto-app), kept pure so they can be unit-tested.

export const MAX_VIDEO_SECONDS = 60;
export const VIDEO_TOO_LONG_MESSAGE = "Відео може тривати до 60 секунд.";
export const MAX_FILE_BYTES = 25 * 1024 * 1024;
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
export const HEART = "heart";

// What the file picker offers; the server validates extension + MIME + magic bytes again.
export const FILE_ACCEPT =
  ".pdf,.doc,.docx,.xls,.xlsx,.txt,application/pdf,application/msword," +
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel," +
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain";
export const MEDIA_ACCEPT = "image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime";

export const isOptimistic = (m) =>
  String(m?.id || "").startsWith("tmp") || m?.status === "sending" || m?.status === "uploading" || m?.status === "failed";

export const messageKey = (m) => m.id || (m.clientId ? `c:${m.clientId}` : String(m.createdAt));

const byTime = (a, b) =>
  Number(isOptimistic(a)) - Number(isOptimistic(b)) ||
  Number(a.createdAt) - Number(b.createdAt) ||
  messageKey(a).localeCompare(messageKey(b));

/** Merge server messages (page / older page / poll) into the list: no duplicates, optimistic twins replaced by clientId. */
export function mergeMessages(prev, incoming) {
  if (!incoming || !incoming.length) return prev;
  const clientIds = new Set(incoming.map((m) => m.clientId).filter(Boolean));
  const map = new Map();
  for (const m of prev) {
    if (isOptimistic(m) && m.clientId && clientIds.has(m.clientId)) continue;
    map.set(messageKey(m), m);
  }
  for (const m of incoming) map.set(messageKey(m), m);
  return Array.from(map.values()).sort(byTime);
}

/** Replace already-present messages with their changed server version (reactions, edit, unsend, finished transcode). */
export function applyChanges(prev, changes) {
  if (!changes || !changes.length) return prev;
  const changed = new Map(changes.map((m) => [messageKey(m), m]));
  let touched = false;
  const next = prev.map((m) => {
    const fresh = changed.get(messageKey(m));
    if (!fresh) return m;
    touched = true;
    return { ...fresh };
  });
  return touched ? next : prev;
}

export function reconcileSent(prev, clientId, stored) {
  const rest = prev.filter((m) => !(m.clientId === clientId && isOptimistic(m)));
  return mergeMessages(rest, [{ ...stored, clientId: stored.clientId || clientId }]);
}

export const patchLocal = (prev, clientId, patch) =>
  prev.map((m) => (m.clientId === clientId && isOptimistic(m) ? { ...m, ...patch } : m));

export function lastServerCreatedAt(messages) {
  let max = 0;
  for (const m of messages) if (!isOptimistic(m) && Number(m.createdAt) > max) max = Number(m.createdAt);
  return max;
}

export function primaryAttachment(m) {
  if (!m || m.type === "unsent") return null;
  if (m.attachments && m.attachments[0]) return m.attachments[0];
  const url = m.mediaUrl || m.previewUrl;
  if (m.type === "image" && url) return { type: "image", url: m.mediaUrl || url, thumbnailUrl: m.previewUrl };
  return null;
}

export function formatFileSize(bytes) {
  const n = Number(bytes || 0);
  if (!n) return "";
  if (n < 1024) return `${n} Б`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} КБ`;
  const mb = n / (1024 * 1024);
  return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} МБ`;
}

export function formatDuration(seconds) {
  const total = Math.max(0, Math.round(Number(seconds || 0)));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

export function validateVideoDuration(seconds) {
  if (!seconds || seconds <= 0) return null;
  return seconds > MAX_VIDEO_SECONDS + 0.75 ? VIDEO_TOO_LONG_MESSAGE : null;
}

/** Fast client-side checks before an upload (the server repeats them). Returns an error text or null. */
export function validateUploadFile(file) {
  if (!file) return "Файл не вибрано.";
  const name = String(file.name || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() : "";
  const isImage = file.type.startsWith("image/");
  const isVideo = file.type.startsWith("video/") || ["mp4", "mov", "m4v"].includes(ext);
  const docExt = ["pdf", "doc", "docx", "xls", "xlsx", "txt"].includes(ext);

  if (!isImage && !isVideo && !docExt) return "Цей тип файлу не підтримується.";
  if (isImage && file.size > MAX_IMAGE_BYTES) return "Фото завелике (максимум 10 МБ).";
  if (isVideo && file.size > MAX_VIDEO_BYTES) return "Відео завелике (максимум 100 МБ).";
  if (!isImage && !isVideo && file.size > MAX_FILE_BYTES) return "Файл завеликий (максимум 25 МБ).";
  return null;
}

export const kindOfFile = (file) => {
  const name = String(file?.name || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() : "";
  if ((file?.type || "").startsWith("image/")) return "image";
  if ((file?.type || "").startsWith("video/") || ["mp4", "mov", "m4v"].includes(ext)) return "video";
  return "file";
};

/* ------------------------------ reply ------------------------------ */

const snippet = (text, max = 140) => {
  const t = String(text || "").replace(/\s+/g, " ").trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
};

export function replyPreviewOf(m) {
  const att = primaryAttachment(m);
  return {
    id: m.id,
    createdAt: Number(m.createdAt),
    senderId: m.senderId,
    kind: att ? att.type : "text",
    text: snippet(m.text),
    thumbnailUrl: att && att.type !== "file" ? att.thumbnailUrl || (att.type === "image" ? att.url : undefined) : undefined,
    filename: att && att.type === "file" ? att.filename : undefined,
  };
}

export function replyBody(preview) {
  if (!preview) return "";
  if (preview.unavailable) return "Повідомлення недоступне";
  if (preview.kind === "image") return preview.text ? `Фото · ${preview.text}` : "Фото";
  if (preview.kind === "video") return preview.text ? `Відео · ${preview.text}` : "Відео";
  if (preview.kind === "file") return preview.filename || "Файл";
  return preview.text || "Повідомлення";
}

export const canReplyTo = (m) =>
  !!m && m.type !== "unsent" && !isOptimistic(m) && !!m.id && !String(m.id).startsWith("tmp");

/* ---------------------------- reactions ---------------------------- */

export const hasReaction = (m, userId, reaction = HEART) =>
  !!userId && !!(m.reactions || []).some((r) => r.reaction === reaction && r.userIds.includes(userId));

export function withReaction(m, userId, add, reaction = HEART) {
  const list = (m.reactions || []).map((r) => ({ ...r, userIds: [...r.userIds] }));
  const entry = list.find((r) => r.reaction === reaction);
  if (add) {
    if (entry) {
      if (!entry.userIds.includes(userId)) {
        entry.userIds.push(userId);
        entry.count = entry.userIds.length;
      }
    } else {
      list.push({ reaction, userIds: [userId], count: 1 });
    }
  } else if (entry) {
    entry.userIds = entry.userIds.filter((id) => id !== userId);
    entry.count = entry.userIds.length;
  }
  return { ...m, reactions: list.filter((r) => r.count > 0) };
}

export const makeClientId = (() => {
  let seq = 0;
  return () => {
    seq += 1;
    return `w${Date.now().toString(36)}${seq.toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  };
})();

/** Duration of a local video file (seconds) via the browser itself; null when it cannot be read. */
export function readVideoDuration(file) {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const video = document.createElement("video");
      video.preload = "metadata";
      const done = (value) => {
        URL.revokeObjectURL(url);
        resolve(value);
      };
      video.onloadedmetadata = () => done(Number.isFinite(video.duration) ? video.duration : null);
      video.onerror = () => done(null);
      video.src = url;
    } catch {
      resolve(null);
    }
  });
}
